import { DropDownDirective } from '../helpers/drop-down.directive';
import { EmptyStateComponent } from './components/empty-state/empty-state.component';
import { NgModule } from '@angular/core';

// Helpers
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { environment } from 'src/environments/environment';
import { RecaptchaModule, RecaptchaSettings, RECAPTCHA_SETTINGS, RECAPTCHA_LANGUAGE } from 'ng-recaptcha';
import { TranslateModule } from '@ngx-translate/core';
import { SidenavOverComponent } from './components/sidenav-over/sidenav-over.component';

import { LottieModule } from 'ngx-lottie';
import player from 'lottie-web';

import { Overlay, BlockScrollStrategy, OverlayContainer, FullscreenOverlayContainer } from '@angular/cdk/overlay';
import { CommonModule, registerLocaleData } from '@angular/common';

export function scrollFactory(overlay: Overlay): () => BlockScrollStrategy {
  return () => overlay.scrollStrategies.block();
}

// Note we need a separate function as it's required
// by the AOT compiler.
export function playerFactory() {
  return player;
}

import { TreeSelectionComponent } from './components/tree-selection/tree-selection.component';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { CustomPaginator } from '../helpers/CustomPaginatorConfiguration';
import { UserDetailsComponent } from './components/users/user-details/user-details.component';
import { AccessControlDirective } from '../helpers/access-control.directive';
import { TreeFieldComponent } from './components/tree-field/tree-field.component';
import { FileManagerComponent } from './components/file-manager/file-manager.component';
import { FilesViewerComponent } from './components/files-viewer/files-viewer.component';
import { TruncateTextPipe } from '../helpers/truncate-text.pipe';
import { MAT_SELECT_SCROLL_STRATEGY } from '@angular/material/select';
import { RouterModule } from '@angular/router';
import { VerifyMobileComponent } from './components/verify-mobile/verify-mobile.component';
import { OtpInputComponent } from './components/otp-input/otp-input.component';
import { KeysPipe } from '../helpers/keys.pipe';
import { CounterDirective } from '../helpers/counter.directive';
import { TimeDiffPipe } from '../helpers/time-left.pipe';
import { PercentageDirective } from '../helpers/percentage.directive';
import { Timezonepipe } from '../helpers/timezone.pipe';
import { LazyImgDirective } from '../helpers/lazy-img.directive';
import { SubmitReasonComponent } from './components/submit-reason/submit-reason.component';
import { JsonParsePipe } from '../helpers/json-parse.pipe';
import { HeaderComponent } from '../layout/admin-layout/header/header.component';
import { TimeRemainingDirective } from '../helpers/TimeRemaining.directive';
import { TimeZoneUtcPipe } from '../helpers/time-zone-utc.pipe';
import { DateSuffixPipe } from '../helpers/date-suffix.pipe';
import { FooterComponent } from '../layout/admin-layout/footer/footer.component';
import { SafePipe } from '../helpers/SafePipe';
import { ReplaceOembedDirective } from '../helpers/replace-oembed.directive';
import { OnlyenglishDirective } from '../helpers/onlyenglish.directive';
import { OnlyarabicDirective } from '../helpers/onlyarabic.directive';
import { DndDirective } from 'src/app/helpers/dnd.directive';
import { DemoMaterialModule } from 'src/app/helpers/material-modules';
import { OnlynumberDirective } from 'src/app/helpers/onlynumber.directive';
import { PrintDirective } from 'src/app/helpers/print.directive';
import { SanitizeHtmlPipePipe } from 'src/app/helpers/sanitize-html-pipe.pipe';
import { ShortNamePipe } from 'src/app/helpers/shortName.pipe';
import { DisableRightClickDirective } from '../helpers/disable-right-click.directive';
import { PaginationComponent } from './components/pagination/pagination.component';
import { UsersListSharedComponent } from './components/users/users-list-shared/users-list-shared.component';
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import { ConfirmationDialogComponent } from './components/confirmation-dialog/confirmation-dialog.component';
import { AttachmentDetailsComponent } from './components/attachment-details/attachment-details.component';
import { LightGalleryVideoComponent } from './components/light-gallery-video/light-gallery-video.component';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { PdfViewerModule } from 'ng2-pdf-viewer';


const components = [
  HeaderComponent,
  FooterComponent,
  EmptyStateComponent,
  PaginationComponent,
  SidenavOverComponent,
  TreeSelectionComponent,
  UsersListSharedComponent,
  UserDetailsComponent,
  TreeFieldComponent,
  FilesViewerComponent,
  FileManagerComponent,
  VerifyMobileComponent,
  OtpInputComponent,
  SubmitReasonComponent,
  ConfirmationDialogComponent,
  AttachmentDetailsComponent,
  LightGalleryVideoComponent
];

const directives = [
  DndDirective,
  OnlynumberDirective,
  PercentageDirective,
  PrintDirective,
  DropDownDirective,
  AccessControlDirective,
  CounterDirective,
  LazyImgDirective,
  TimeRemainingDirective,
  ReplaceOembedDirective,
  OnlyenglishDirective,
  OnlyarabicDirective,
  DisableRightClickDirective
];

const pipes = [
  SanitizeHtmlPipePipe,
  ShortNamePipe,
  TruncateTextPipe,
  KeysPipe,
  TimeDiffPipe,
  Timezonepipe,
  JsonParsePipe,
  TimeZoneUtcPipe,
  DateSuffixPipe,
  SafePipe,

];

const modules = [
  DemoMaterialModule,
  InfiniteScrollModule,
  ReactiveFormsModule,
  FormsModule,
  RecaptchaModule,
  NgxIntlTelInputModule,
  // NgxMaterialTimepickerModule,//
  // NgxScannerQrcodeModule,
  // QRCodeModule,
  // NgxChartsModule,
  // CarouselModule,
  PdfViewerModule,
  // CKEditorModule
];

@NgModule({
  declarations: [
    // Directives
    directives,

    // Pipes
    pipes,
    // Components
    components,
  ],
  imports: [
    CommonModule,
    modules,
    TranslateModule.forChild({}),
    LottieModule.forRoot({ player: playerFactory }),
    RouterModule,
  ],
  exports: [
    // Components
    components,
    directives,
    pipes,
    modules,
    TranslateModule,
    LottieModule,
  ],
  providers: [
    {
      provide: RECAPTCHA_SETTINGS,
      useValue: {
        siteKey: environment.googleSiteKey,
        useValue: localStorage.getItem('language') || 'en'
      } as RecaptchaSettings,
    },
    {
      provide: RECAPTCHA_LANGUAGE,
      useValue: localStorage.getItem('language') || 'en', // use language
    },
    { provide: MatPaginatorIntl, useValue: CustomPaginator() },
    { provide: MAT_SELECT_SCROLL_STRATEGY, useFactory: scrollFactory, deps: [Overlay] },
    { provide: OverlayContainer, useClass: FullscreenOverlayContainer }

  ]
})
export class SharedModule { }
